import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Theme } from '@mui/material/styles'
import { useFormikContext } from 'formik'

import type {
  EditableCustomFieldProps,
  ICustomFieldValueFormModel,
} from './typings'
import { useGetError } from './useGetError'
import { useHideOptional } from './useHideOptional'
import debounce from '@mui/material/utils/debounce'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'

const styles = {
  root: {
    color: 'rgb(165, 39, 39)',
  },

  error: {
    color: 'rgb(165, 39, 39)',
  },
}

export function EditableCustomDate(props: EditableCustomFieldProps) {
  const {
    margin,
    field: { id: fieldId, name },
    fieldSetting: { required } = {},
    textFieldProps,
    whiteBackground = false,
    avoidValidateOnBlur = false,
  } = props
  const { getFormat } = useLocalizationDateFormat()

  const {
    setFieldValue,
    initialValues: {
      customFields: { date },
    },
    handleBlur,
  } = useFormikContext<ICustomFieldValueFormModel>()

  const initialFieldValue = useMemo(() => {
    if (date[fieldId]) {
      if (date[fieldId].value) return new Date(date[fieldId].value!)
    }
    return null
  }, [date, fieldId])

  const [value, setValue] = useState(initialFieldValue)

  const update = useMemo(
    () =>
      debounce((d: Date | null) =>
        setFieldValue(`customFields.date[${fieldId}].value`, d),
      ),
    [fieldId, setFieldValue],
  )

  useEffect(() => {
    update.clear()
    update(value)
  }, [update, value])

  useEffect(() => setValue(initialFieldValue), [initialFieldValue])
  const { errorText, hasError: fieldError } = useGetError(props)

  const helperText = fieldError ? errorText : ``

  const showField = useHideOptional(props)

  if (!showField) return null

  return (
    <DatePicker
      label={name}
      value={value}
      format={getFormat()}
      onChange={setValue}
      slotProps={{
        textField: {
          error: fieldError,
          margin: margin,
          helperText: helperText,
          required: required,
          name: `customFields.date[${fieldId}]`,
          InputProps: {
            error: fieldError,
            sx: {
              backgroundColor: whiteBackground
                ? (theme: Theme) => theme.palette.common.white
                : 'inherit',
              ...(fieldError && styles.error),
            },
          },
          inputProps: {
            'data-test-id': `custom-date-field-${fieldId}`,
          },
          InputLabelProps: {
            sx: { ...(fieldError && styles.error) },
          },
          FormHelperTextProps: { sx: styles },
          sx: {
            width: '100%',
          },

          onBlur: !avoidValidateOnBlur ? handleBlur : undefined,
          ...textFieldProps,
        },
      }}
    />
  )
}
